// What? A modal for refining document type section prompts using AI
// Why? To improve prompt quality by comparing generated content with correct examples
// How? The component's logic flow:
// 1. Select a previously generated document
// 2. Upload a file with the correct/ideal content
// 3. Submit to backend which compares and refines the prompt(s)
// 4. Return refined prompt(s) to parent component

import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  CircularProgress,
  DialogTitle,
  Modal,
  ModalDialog,
  Select,
  Option,
  Stack,
  Typography,
} from "@mui/joy";
import {
  usePromptRefinement,
  DocumentBasicInfo,
} from "../../hooks/usePromptRefinement";
import FileUploader from "./FileUploader";
import { UploadFileReference, ExistingFile } from "../../types/documentTypes";
import { UploadedRefinementData } from "../documentType/tabs/StructureTab";
import { useWorkspaceContext } from "../../../../providers/WorkspaceProvider";

// Type for the refined data passed back
export type RefinedData = {
  sectionId: number;
  promptText: string;
};

interface PromptRefinementModalProps {
  isOpen: boolean;
  onClose: () => void;
  documentTypeId: number;
  sectionId?: number; // Optional: Pass if refining a single section
  onSuccess: (refinedData?: RefinedData) => void; // Callback on successful refinement
  uploadedRefinementData: UploadedRefinementData | null;
  setUploadedRefinementData: (data: UploadedRefinementData | null) => void;
  selectedGeneratedDocId: number | null;
  setSelectedGeneratedDocId: (id: number | null) => void;
}

const PromptRefinementModal: React.FC<PromptRefinementModalProps> = ({
  isOpen,
  onClose,
  documentTypeId,
  sectionId,
  onSuccess,
  uploadedRefinementData,
  setUploadedRefinementData,
  selectedGeneratedDocId,
  setSelectedGeneratedDocId,
}) => {
  const { workspace } = useWorkspaceContext();
  const {
    availableDocuments,
    isLoadingDocuments,
    documentsError,
    refinePrompts,
    isRefining,
    refinementError,
    refinementSuccess,
    refinedSectionId,
    refinedPromptText,
  } = usePromptRefinement(workspace?.id || "", documentTypeId);

  const [internalError, setInternalError] = useState<string | null>(null);
  const [fileUploadError, setFileUploadError] = useState<string | null>(null);

  // Clear local errors state when modal opens/closes or relevant IDs change
  useEffect(() => {
    if (isOpen) {
      setInternalError(null);
      setFileUploadError(null);
    }
  }, [isOpen, documentTypeId, sectionId]);

  // Handle successful submission and pass back data
  useEffect(() => {
    if (refinementSuccess) {
      let dataToPass: RefinedData | undefined = undefined;
      // Check if the hook has refined data (relevant for single section mode)
      if (
        refinedSectionId &&
        refinedPromptText &&
        refinedSectionId === sectionId
      ) {
        dataToPass = {
          sectionId: refinedSectionId,
          promptText: refinedPromptText,
        };
      }
      // Call parent success handler with refined data (or undefined if bulk/no change)
      onSuccess(dataToPass);
      onClose(); // Close the modal regardless
    }
    // Add hook state variables to dependency array
  }, [
    refinementSuccess,
    refinedSectionId,
    refinedPromptText,
    sectionId,
    onSuccess,
    onClose,
  ]);

  // Callback for FileUploader when a file is successfully uploaded
  const handleFileReferenceReceived = (fileRef: UploadFileReference) => {
    if (
      fileRef &&
      typeof fileRef.fileId === "string" &&
      typeof fileRef.parsedContent === "string"
    ) {
      setUploadedRefinementData({
        fileId: fileRef.fileId,
        fileName: fileRef.originalName,
        parsedContent: fileRef.parsedContent,
      });
      setFileUploadError(null);
    } else {
      console.error(
        "Received invalid file reference structure from FileUploader callback",
        fileRef,
      );
      setFileUploadError(
        "Upload succeeded but received invalid file reference structure.",
      );
      setUploadedRefinementData(null);
    }
  };

  // Define a dummy ID for the FileUploader's existing file representation
  const DUMMY_REFINEMENT_FILE_ID = -1;

  // Callback for FileUploader when a file is deleted
  const handleFileDelete = (idToDelete: number) => {
    if (idToDelete === DUMMY_REFINEMENT_FILE_ID) {
      setUploadedRefinementData(null); // Clear the full data object
      setFileUploadError(null);
    }
  };

  const handleSubmit = () => {
    setInternalError(null);
    setFileUploadError(null);
    if (!selectedGeneratedDocId) {
      setInternalError("Please select a generated document.");
      return;
    }
    if (!uploadedRefinementData || !uploadedRefinementData.parsedContent) {
      setInternalError(
        "Please upload a valid file containing the correct example.",
      );
      return;
    }

    refinePrompts({
      documentTypeId,
      generatedDocumentId: selectedGeneratedDocId,
      correct_file_content: uploadedRefinementData.parsedContent,
      sectionId,
    });
  };

  // Combine hook error, internal validation error, and file upload error
  const displayError = refinementError || internalError || fileUploadError;

  // Format existing file data for FileUploader using the dummy ID
  const existingFileForUploader: ExistingFile[] = uploadedRefinementData
    ? [
        {
          id: DUMMY_REFINEMENT_FILE_ID,
          fileName: uploadedRefinementData.fileName, // Display the original filename
          fileSize: 0, // Size isn't critical here, maybe get from state if needed
          url: "",
        },
      ]
    : [];

  return (
    <Modal open={isOpen} onClose={onClose}>
      <ModalDialog sx={{ maxWidth: 600, width: "90%" }}>
        <DialogTitle>
          {sectionId ? "Refine Section Prompt" : "Refine All Section Prompts"}
        </DialogTitle>
        <Stack spacing={3}>
          <Typography level="body-sm">
            Select a previously generated document and upload a file containing
            the ideal ('correct') content. The system will compare the generated
            section(s) with the correct example to refine the prompt(s).
          </Typography>

          {/* Generated Document Select */}
          <Box>
            <Typography level="title-sm" sx={{ mb: 1 }}>
              Generated Document:
            </Typography>
            {isLoadingDocuments && (
              <Box
                sx={{ display: "flex", alignItems: "center", gap: 1, py: 2 }}
              >
                <CircularProgress size="sm" />
                <Typography level="body-sm">Loading documents...</Typography>
              </Box>
            )}
            {documentsError && (
              <Typography color="danger" level="body-sm">
                Error loading documents: {documentsError}
              </Typography>
            )}
            {!isLoadingDocuments && !documentsError && (
              <Select
                value={selectedGeneratedDocId}
                onChange={(_, value) =>
                  setSelectedGeneratedDocId(value as number | null)
                }
                placeholder="-- Select a Generated Document --"
                disabled={isRefining}
              >
                {availableDocuments.map((doc: DocumentBasicInfo) => (
                  <Option key={doc.DocumentID} value={doc.DocumentID}>
                    {doc.Name} (ID: {doc.DocumentID})
                  </Option>
                ))}
              </Select>
            )}
            {availableDocuments.length === 0 &&
              !isLoadingDocuments &&
              !documentsError && (
                <Typography level="body-sm" color="neutral">
                  No previously generated documents found for this type.
                </Typography>
              )}
          </Box>

          {/* File Upload */}
          <Box>
            <Typography level="title-sm" sx={{ mb: 1 }}>
              Correct Example File:
            </Typography>
            <FileUploader
              uploadEndpoint="/admin/refinement_files/upload"
              documentType="RefinementExample"
              maxFiles={1}
              maxFilesPerUpload={1}
              existingFiles={existingFileForUploader}
              onFileReferenceReceived={handleFileReferenceReceived}
              onDeleteFile={handleFileDelete}
              onUploadError={setFileUploadError}
              requireFileReference={true}
              label="Drop correct example file here or click to upload"
              accept={{
                "text/plain": [".txt"],
                "text/markdown": [".md"],
                "application/pdf": [".pdf"],
                "application/msword": [".doc"],
                "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
                  [".docx"],
              }}
            />
          </Box>

          {/* Error Display */}
          {displayError && (
            <Typography color="danger" level="body-sm">
              Error: {displayError}
            </Typography>
          )}

          {/* Action Buttons */}
          <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
            <Button
              variant="outlined"
              color="neutral"
              onClick={onClose}
              disabled={isRefining}
            >
              Cancel
            </Button>
            <Button
              variant="solid"
              color="primary"
              onClick={handleSubmit}
              disabled={
                isRefining || !selectedGeneratedDocId || !uploadedRefinementData
              }
              loading={isRefining}
            >
              {isRefining ? "Refining..." : "Refine Prompt(s)"}
            </Button>
          </Box>
        </Stack>
      </ModalDialog>
    </Modal>
  );
};

export default PromptRefinementModal;
